import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { BookOpen, Clock, CheckCircle, ArrowLeft, Play } from 'lucide-react';
import { memberApiFetch, isAuthenticated } from '@/lib/memberAuth';
import { PageContainer, PageHeader, PageState } from '@/components/weavein';

interface Course {
  id: number;
  title: string;
  description: string | null;
  thumbnail_url: string | null;
  is_published: boolean;
  module_count: number;
  lesson_count: number;
}

export default function MemberCoursesPage() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [gated, setGated] = useState(false);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/member/login');
      return;
    }
    loadCourses();
  }, [navigate]);

  const loadCourses = async () => {
    setLoading(true);
    setError('');
    setGated(false);
    try {
      // memberApiFetch sends the member token AND the active-org X-CBO-ID header
      // that member-portal endpoints scope to (mirrors the training/inbox pages).
      const res = await memberApiFetch('/lms/courses');
      if (res.status === 403) {
        // Learning isn't enabled for the member's active org — show a calm
        // gated state instead of a scary "failed to load" error.
        setGated(true);
        return;
      }
      if (!res.ok) throw new Error('Failed to load courses');
      const data = await res.json();
      setCourses(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err.message || 'Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const header = (
    <PageHeader
      leading={
        <Link to="/member/home">
          <Button variant="ghost" size="sm" className="gap-1">
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
        </Link>
      }
      title="Learning"
      description={<>{courses.length} course{courses.length !== 1 ? 's' : ''} available</>}
    />
  );

  if (loading) {
    return (
      <>
        {header}
        <PageContainer size="wide">
          <PageState variant="loading" message="Loading courses…" />
        </PageContainer>
      </>
    );
  }

  return (
    <>
      {header}
      <PageContainer size="wide">
        {gated && (
          <PageState
            variant="empty"
            title="Learning isn’t enabled yet"
            message="Courses aren’t available for your organization yet. Check back soon."
          />
        )}

        {error && !gated && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        {courses.length === 0 && !error && !gated && (
          <div className="text-center py-16">
            <BookOpen className="h-10 w-10 text-ink-300 mx-auto mb-3" />
            <p className="text-ink-500">No courses available yet.</p>
          </div>
        )}

        <div className="grid gap-4">
          {courses.map((course) => (
            <Card
              key={course.id}
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/member/courses/${course.id}`)}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-base leading-snug">{course.title}</CardTitle>
                  <Badge variant="secondary" className="shrink-0 text-xs">
                    {course.lesson_count} lesson{course.lesson_count !== 1 ? 's' : ''}
                  </Badge>
                </div>
                {course.description && (
                  <CardDescription className="text-sm line-clamp-2">{course.description}</CardDescription>
                )}
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 text-xs text-ink-500">
                    <span className="flex items-center gap-1">
                      <BookOpen className="h-3 w-3" />
                      {course.module_count} module{course.module_count !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <Button size="sm" className="gap-1 text-xs h-7">
                    <Play className="h-3 w-3" />
                    Start
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </PageContainer>
    </>
  );
}
